import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Text,
  VStack,
  FormControl,
  Input,
  FormHelperText,
  FormLabel,
  FormErrorMessage,
  useDisclosure,
  Radio,
  RadioGroup,
  Stack,
  Spinner,  
  Textarea
} from '@chakra-ui/react'
import { useReferralProvider } from 'providers/referralsProvider/ReferralProvider'
import { settingValidate } from './personalDataValidation'
import { IReferredData } from '../../providers/referralsProvider/ReferralProvider.d'
import { IPostReferrals, IResponseReferrals } from 'services/IApi'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { isInternalErrorStatus, isSuccessStatus } from 'utils/functions/validations'
import { normalizeString } from 'utils/functions/formatString'

import FooterReferrals from 'components/footerReferrals/FooterReferrals'
import ModalReferrals from '../../components/modalReferrals/modalReferrals'
import SearchSelect from '../../components/searchSelect/searchSelect'
import ToastComponent from '../../components/customToast/toast'
import usePostReferralshWithRecaptcha from 'services/hooks/useReferralshWithRecaptchat'
import { getTermsAndConditionsWithRecaptcha } from 'services/api'

interface Bank {
  name: string
  icon: string
}

const ReferralPersonalData = () => {
  const navigate = useNavigate()
  const { showErrorToast, closeToast } = ToastComponent()
  const { referrals, referralAction, isRegisteringReferral, editReferredData, companyData, subscriberData, cleanProvider } = useReferralProvider()
  const { isOpen: isOpenModal, onOpen: onOpenModal, onClose } = useDisclosure()
  const [option, setOption] = useState<string>('true')
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null)
  const [showRequiredMessage, setShowRequiredMessage] = useState<boolean>(false)
  const [isTyCChecked, setIsTyCChecked] = useState<boolean>(false)
  const [documentTyC, setDocumentTyC] = useState<string>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const referralshWithRecaptcha = usePostReferralshWithRecaptcha()

  const { executeRecaptcha } = useGoogleReCaptcha()

  const showTextEmail = 'Ingresá tu email corporativo'
  const status409 = 409;
  const errorSaveInfo = 'No podemos guardar la información. Volvé a intentarlo.'

  const {
    register,
    setValue,
    getValues,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<IReferredData>({
    defaultValues: { bankRepresentative: true, },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  const handleBankSelect = useCallback( (bank: Bank | null) => {
    setSelectedBank(bank)
    setValue('bankName', bank?.name || null)
    setShowRequiredMessage(false)
  }, [setSelectedBank,setShowRequiredMessage])

  const isOptionAndNoSelectedBank = useMemo( () =>  (option && !selectedBank), [option, selectedBank])

  const handleBankBlur = useCallback( () => {
    if (isOptionAndNoSelectedBank) {
      setValue('bankName', null, { shouldValidate: true })
      setShowRequiredMessage(true)
    }
  }, [isOptionAndNoSelectedBank, setShowRequiredMessage])

  
  const isInvalid = useMemo( () => !isValid || isOptionAndNoSelectedBank || !isTyCChecked || isRegisteringReferral
  , [isValid, isOptionAndNoSelectedBank, isTyCChecked, isRegisteringReferral])

  const isFormValid = useCallback((): boolean => !isInvalid,[isInvalid])


  const resetValidationCuit = useCallback(async e => clearErrors([e.target.name]), [clearErrors])

  const onSubmit = useCallback(async () => {
    setLoading(true)
    if (isRegisteringReferral) {
      setLoading(false)
      return
    }
    referralAction('referring')
    const referredData: IReferredData = {
      name: getValues('name').trim(),
      surname: getValues('surname').trim(),
      dni: getValues('dni'),
      email: getValues('email'),
      comments: getValues('comments').trim(),
    }
    editReferredData(referredData)
    handleSubmit({
      ...referredData,
      cuit: companyData.cuit,
      bankRepresentative: getValues('bankRepresentative'),
      bankName: getValues('bankName'),
      termsAndConditions: documentTyC,
      referredContactInfo: {
        name: subscriberData.name,
        surname: subscriberData.surname,
        email: subscriberData.email,
        phoneNumber: subscriberData.phoneNumber,
      }
    })
  }, [companyData, documentTyC, subscriberData, isRegisteringReferral, setLoading])

  const handleSubmit = useCallback(async (dataReferrals: IPostReferrals) => {
    if (isRegisteringReferral) {
      setLoading(false)
      return
    }
    if (!executeRecaptcha) {
      setLoading(false)
      console.info('Execute recaptcha not yet available')
      referralAction('withoutReferral')
      return
    }
    const recaptcha: string = await executeRecaptcha('referralpersonaldata')
    const respuesta: IResponseReferrals = await referralshWithRecaptcha.mutateAsync({ token: recaptcha, data: dataReferrals })
    if (isSuccessStatus(respuesta.status)) {
      referralAction('referred')
      setLoading(false)
      onOpenModal()
    } else if (isInternalErrorStatus(respuesta.status)) {
      setLoading(false)
      referralAction('withoutReferral')
      showErrorToast(errorSaveInfo)
      return
    } else if (respuesta.status === status409) {
      setLoading(false)
      navigate('/referido', { state: 'LIMIT_EXCEEDED' })
    } else {
      setLoading(false)
      referralAction('withoutReferral')
    }
    setLoading(false)
    closeToast()
  }, [executeRecaptcha, isRegisteringReferral, setLoading])

  const handleReferredModal = useCallback(async () => {
    onClose()
    cleanProvider()
    navigate('/referir-empresa')
  },[])


  const onChangeDocumento = useCallback(async e => {
    const numbers = e.target.value.match(/\d+/g);
    const result: string = normalizeString(numbers ? numbers.join('') : '');
    setValue('dni', result, { shouldValidate: true });
  },[])

  const onChangeBankRepresentative = useCallback(async (value: string) => {
    setOption(value)
    if (value === '') {
      setSelectedBank(null)
      setValue('bankRepresentative', false)
    } else {
      setValue('bankRepresentative', true)
    }
  },[setOption, setSelectedBank])

  const onChangeComments = useCallback(async e => {
    const validation = e.target.value.match(/[a-zA-Z\d.,@\s]+/g);
    const result: string = validation ? validation.join('') : '';
    setValue('comments', result, { shouldValidate: true });
  },[])


  // TyC
  const handlerTYC = useCallback(async () => {
    const recaptchaToken = await executeRecaptcha('referrals_handlerTYC')
    if (!recaptchaToken) {
      console.error('Error al obtener el token de reCaptcha',recaptchaToken)
      return
    }
    try {
      const response = await getTermsAndConditionsWithRecaptcha(recaptchaToken)
      if (isSuccessStatus(response.status)) {        
        setDataDocument(response.data) 
      } else {
        console.error('No se pudo obtener el archivo de términos y condiciones')
      }
    } catch (error) {
      console.error('Error al descargar los términos y condiciones:', error)
    }
  },[executeRecaptcha])


  const setDataDocument = useCallback(async data => {
    if (data instanceof Blob) {
      const reader = new FileReader()
      reader.onload = () => {
      try {
        let base64Data
        if (typeof reader.result === 'string') {
          const json = JSON.parse(reader.result)
          base64Data = json.data
        } else if (reader.result instanceof ArrayBuffer) {
          const bytes = new Uint8Array(reader.result)
          base64Data = btoa(String.fromCharCode(...bytes))
        } else {
          throw new Error('Tipo de resultado inesperado')
        }
        if (/[^A-Za-z0-9+/=]/.test(base64Data)) {
          console.error('Los datos en base64 contienen caracteres no válidos')
          return
        }
          setDocumentTyC(base64Data)
        } catch (error) {
          console.error('Error al decodificar la cadena base64:', error)
        }
      }
      reader.readAsText(data)
    } else if ((typeof data !== 'string') || (typeof data === 'string' && data !=='')) {
        console.error('Se esperaba un Blob pero se recibió:', `${data} de tipo ${typeof data}`)
    } else {
      return
    }   
  }, [setDocumentTyC])


  const onChangeTyC = useCallback(async (e: boolean) => setIsTyCChecked(e), [setIsTyCChecked])


  useEffect(() => {
    if (!subscriberData) {
      navigate('/referir-empresa')
    }
  }, [])

  useEffect(() => { handlerTYC() }, [executeRecaptcha])


  return (
    <>
      <form data-testid='personalData-form'>
        <Box maxW='400px' w='100%' margin='auto' marginBottom={'120px'} pt='64px' height='100%'>
          <Text textStyle='h2Bold' textAlign='center' color='neutral.130'>
            Referí una empresa
          </Text>
          <Text textStyle='bodyMd' textAlign='center' color='neutral.130'>
            Completá los datos para recomendar Interbanking.
          </Text>
          <Text data-testid='referrals-text' textStyle='bodyMd' textAlign='center' color='neutral.130'>
            Pódes referir hasta {referrals} empresas por día.
          </Text>
          <Text textStyle='titleMdBold' pt='32px' color='neutral.130'>
            Ahora ingresá tus datos
          </Text>
          <VStack spacing='24px' mt='16px' justifyContent='center'>
            <Box w='100%'>
              <VStack spacing='12'>
                <FormControl isInvalid={!!errors.name}>
                  <FormLabel textStyle='labelMd' display='contents' color='grey.120'>
                    Nombre
                  </FormLabel>
                  <Input
                    data-testid='name-input'
                    id='name'
                    placeholder='Nombre'
                    borderColor='neutral.90'
                    onFocus={resetValidationCuit}
                    {...register('name', {
                      ...settingValidate.name,
                      onBlur: e => setValue('name', e.target.value.trim(), { shouldValidate: true })
                    })}
                  />
                  <FormErrorMessage textStyle='labelSm' data-testid='error-message' mt={0}>
                    {errors?.name?.message}
                  </FormErrorMessage>
                </FormControl>
              </VStack>
            </Box>
            <Box w='100%'>
              <VStack spacing='12'>
                <FormControl isInvalid={!!errors.surname}>
                  <FormLabel textStyle='labelMd' display='contents' color='grey.120'>
                    Apellido
                  </FormLabel>
                  <Input
                    data-testid='surname-input'
                    id='surname'
                    placeholder='Apellido'
                    borderColor='neutral.90'
                    onFocus={resetValidationCuit}
                    {...register('surname', {
                      ...settingValidate.surname,
                      onBlur: e => setValue('surname', e.target.value.trim(), { shouldValidate: true })
                    })}
                  />
                  <FormErrorMessage textStyle='labelSm' data-testid='error-message' mt={0}>
                    {errors?.surname?.message}
                  </FormErrorMessage>
                </FormControl>
              </VStack>
            </Box>
            <Box w='100%'>
              <VStack spacing='12'>
                <FormControl isInvalid={!!errors.dni}>
                  <FormLabel textStyle='labelMd' display='contents' color='grey.120'>
                    Documento
                  </FormLabel>
                  <Input
                    data-testid='dni-input'
                    id='dni'
                    placeholder='Documento'
                    borderColor='neutral.90'
                    type='text'
                    maxLength={15}
                    onFocus={resetValidationCuit}
                    {...register('dni', settingValidate.dni)}
                    onChange={onChangeDocumento}
                  />
                  <FormErrorMessage textStyle='labelSm' data-testid='error-message' mt={0}>
                    {errors?.dni?.message}
                  </FormErrorMessage>
                </FormControl>
              </VStack>
            </Box>
            <Box w='100%'>
              <Text textStyle='titleSmBold' color='grey.120'>
                ¿Referís en representación de un banco?
              </Text>
              <RadioGroup
                onChange={onChangeBankRepresentative}
                data-testid='radio-group'
                value={option} p='10px 10px 0'>
                <Stack spacing={10} direction='row'>
                  <Radio value='true' size='lg' data-testid='radioButton-true'><Text textStyle='bodyMdSemi'>Sí</Text></Radio>
                  <Radio value='' size='lg' data-testid='radioButton-false'><Text textStyle='bodyMdSemi'>No</Text></Radio>
                </Stack>
              </RadioGroup>
              {option && (
                <FormControl pt='16px'>
                  <SearchSelect onBankSelect={handleBankSelect} onBlur={handleBankBlur} isRequired={showRequiredMessage} />
                </FormControl>
              )}
            </Box>
            <Box w='100%'>
              <VStack>
                <FormControl isInvalid={!!errors.email}>
                  <FormLabel textStyle='labelMd' display='contents' color='grey.120'>
                    Email
                  </FormLabel>
                  <Input
                    data-testid='email-input'
                    id='email'
                    placeholder='Email'
                    borderColor='neutral.90'
                    maxLength={50}
                    onFocus={resetValidationCuit}
                    {...register('email', settingValidate.email)}
                  />
                  {!errors?.email && option ? (
                    <FormHelperText textStyle='labelSm' position='absolute' data-testid='helper-text' bottom='-17px'>
                      {showTextEmail}
                    </FormHelperText>
                  ) : (
                    <FormErrorMessage textStyle='labelSm' data-testid='error-message' mt={0}>
                      {errors?.email?.message}
                    </FormErrorMessage>
                  )}
                </FormControl>
                <ModalReferrals
                  isOpen={isOpenModal}
                  onClose={handleReferredModal}
                  onSubmit={handleReferredModal}
                />
              </VStack>
            </Box>
            <Box w='100%'>
              <VStack>
                <FormControl isInvalid={!!errors.comments}>
                  <FormLabel textStyle='labelMd' display='contents' color='grey.120'>
                    Comentarios (opcional)
                  </FormLabel>
                  <Textarea
                    data-testid='comments-input'
                    id='comments'
                    placeholder='Comentarios'
                    borderColor='neutral.90'
                    size='lg'                    
                    maxLength={150}
                    onFocus={resetValidationCuit}
                    {...register('comments', {
                      ...settingValidate.comments,
                      onBlur: e => setValue('comments', e.target.value.trim(), { shouldValidate: true })
                     })}
                     onChange={onChangeComments}
                  />
                  <FormErrorMessage textStyle='labelSm' data-testid='error-message' mt={0}>
                    {errors?.comments?.message}
                  </FormErrorMessage>
                </FormControl>
              </VStack>
            </Box>
            {loading && <Spinner variant='default' data-testid='spinner' />}
          </VStack>
        </Box>
      </form>
      <FooterReferrals stepNumber='3' textContinue='Referir' disableBack={loading} disableContinue={!isFormValid()} onAction={onSubmit} 
        documentTyC={documentTyC} 
        showTyC
        isTyCChecked={isTyCChecked}
        onChangeTyC={onChangeTyC}
      />
    </>
  )
}

export default ReferralPersonalData
